import { useState, useEffect } from 'react';
import { useData } from '../contexts/DataContext';
import { useAuth } from '../contexts/AuthContext';
import { Loader2, Eye, EyeOff, Check, UserCheck, Percent } from 'lucide-react';
import { ImportExcel } from './ImportExcel';
import { UserAvatar } from './UserAvatar';
import { WorkPercentageModal } from './WorkPercentageModal';
import type { Project, Profile } from '../types';

function ProjectItem({
  project,
  isSaving,
  onUpdate
}: {
  project: Project;
  isSaving?: boolean;
  onUpdate: (name: string, standardText: string, visible: boolean) => Promise<void>;
}) {
  const [localText, setLocalText] = useState(project.standardText || '');
  const [prevStandardText, setPrevStandardText] = useState(project.standardText);
  if (project.standardText !== prevStandardText) {
    setPrevStandardText(project.standardText);
    setLocalText(project.standardText || '');
  }

  const handleBlur = async () => {
    if (localText === (project.standardText || '')) return;
    try {
      await onUpdate(project.name, localText, project.visible ?? false);
    } catch (e) {
      console.error("Failed to update standard text:", e);
    }
  };

  return (
    <li className="flex flex-col gap-2 p-3 rounded-lg border border-slate-200 hover:border-indigo-200 hover:bg-indigo-50/50 transition-colors group">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="relative flex items-center justify-center">
            <input
              type="checkbox"
              id={`project-visible-${project.id}`}
              checked={project.visible ?? false}
              onChange={async (e) => {
                try {
                  await onUpdate(project.name, localText, e.target.checked);
                } catch (err) {
                  console.error("Failed to update visibility:", err);
                }
              }}
              disabled={isSaving}
              className="peer w-6 h-6 appearance-none border-2 border-slate-300 rounded-lg checked:bg-indigo-600 checked:border-indigo-600 focus:ring-2 focus:ring-indigo-500/20 outline-none transition-all duration-200 cursor-pointer disabled:opacity-50 hover:border-indigo-400 checked:hover:bg-indigo-700 checked:hover:border-indigo-700"
            />
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              {project.visible ? (
                <Eye className="w-3.5 h-3.5 text-white animate-in zoom-in duration-200" />
              ) : (
                <EyeOff className="w-3.5 h-3.5 text-slate-400 animate-in zoom-in duration-200" />
              )}
            </div>
          </div>
          <label htmlFor={`project-visible-${project.id}`} className="text-sm font-medium text-slate-700 cursor-pointer select-none">
            {project.name}
          </label>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <label className="text-xs font-medium text-slate-400 whitespace-nowrap">Standard Text:</label>
        <input
          type="text"
          placeholder="e.g. Daily coding"
          value={localText}
          onChange={e => setLocalText(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              (e.target as HTMLInputElement).blur();
            }
          }}
          disabled={isSaving}
          className="flex-1 text-xs px-2 py-1 border border-slate-200 rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 outline-none bg-white transition-all shadow-sm"
        />
      </div>
    </li>
  );
}



export function Settings() {
  const {
    projects,
    updateProject,
    loadingData,
    fetchProfiles,
    updateProfileMasterData,
    updateProfileEmployeeData
  } = useData();

  const { user, role, isSupervisorMode } = useAuth();
  const isSupervisor = typeof isSupervisorMode === 'boolean' ? isSupervisorMode : role === 'supervisor';

  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [masterData, setMasterData] = useState<Record<string, {
    employeeNumber: string;
    initials: string;
    firstName: string;
    lastName: string;
  }>>({});
  const [isSavingMasterData, setIsSavingMasterData] = useState(false);
  const [masterDataSaved, setMasterDataSaved] = useState(false);
  const [selectedPercentageEmployee, setSelectedPercentageEmployee] = useState<Profile | null>(null);

  useEffect(() => {
    if (isSupervisor) {
      fetchProfiles().then(data => {
        setProfiles(data);
        const map: Record<string, {
          employeeNumber: string;
          initials: string;
          firstName: string;
          lastName: string;
        }> = {};
        data.forEach(p => {
          map[p.id] = {
            employeeNumber: p.employeeNumber || '',
            initials: p.initials || '',
            firstName: p.firstName || '',
            lastName: p.lastName || ''
          };
        });
        setMasterData(map);
      }).catch(console.error);
    }
  }, [isSupervisor, fetchProfiles]);

  const handleSaveMasterData = async () => {
    try {
      setIsSavingMasterData(true);
      setMasterDataSaved(false);

      await Promise.all(
        profiles.map(async p => {
          const item = masterData[p.id];
          if (!item) return;
          const cleanEmp = item.employeeNumber.trim() || null;
          const cleanInit = item.initials.trim().toUpperCase() || null;
          const cleanFirst = item.firstName.trim() || null;
          const cleanLast = item.lastName.trim() || null;

          const hasEmpChange = cleanEmp !== (p.employeeNumber || null);
          const hasInitChange = cleanInit !== (p.initials || null);
          const hasFirstChange = cleanFirst !== (p.firstName || null);
          const hasLastChange = cleanLast !== (p.lastName || null);

          if (hasEmpChange || hasInitChange || hasFirstChange || hasLastChange) {
            if (updateProfileMasterData) {
              await updateProfileMasterData(p.id, {
                employeeNumber: cleanEmp,
                initials: cleanInit,
                firstName: cleanFirst,
                lastName: cleanLast
              });
            } else if (updateProfileEmployeeData) {
              await updateProfileEmployeeData(p.id, cleanEmp, cleanInit);
            }
          }
        })
      );

      setProfiles(prev =>
        prev.map(p => {
          const item = masterData[p.id];
          if (!item) return p;
          return {
            ...p,
            employeeNumber: item.employeeNumber.trim() || null,
            initials: item.initials.trim().toUpperCase() || null,
            firstName: item.firstName.trim() || null,
            lastName: item.lastName.trim() || null
          };
        })
      );
      setMasterDataSaved(true);
      setTimeout(() => setMasterDataSaved(false), 3000);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save employee master data';
      alert(message);
    } finally {
      setIsSavingMasterData(false);
    }
  };

  // Filter positions for regular users: assigned or global positions
  const displayPositions = projects.filter(p => {
    if (p.isGlobal) return true;
    if (user?.id && p.members?.includes(user.id)) return true;
    return false;
  });

  if (loadingData) {
    return <div className="flex justify-center p-12"><Loader2 className="w-8 h-8 animate-spin text-indigo-500" /></div>;
  }



  // Supervisor Settings View
  if (isSupervisor) {
    return (
      <div className="max-w-5xl mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Settings</h1>
          <p className="text-slate-500 mt-1">Manage employee master data for your organization.</p>
        </div>

        {/* Employee Master Data: Names, Employee Number & Initials */}
        <section className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-200 bg-slate-50 flex justify-between items-center">
            <div className="flex items-center gap-2.5">
              <UserCheck className="w-5 h-5 text-indigo-600" />
              <div>
                <h2 className="text-base font-bold text-slate-800">Employee Master Data</h2>
                <p className="text-xs text-slate-500">Edit employee details including name, employee numbers, and initials.</p>
              </div>
            </div>
            <span className="text-xs font-semibold bg-indigo-50 text-indigo-700 px-2.5 py-1 rounded-full border border-indigo-100">
              {profiles.length} {profiles.length === 1 ? 'User' : 'Users'}
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="bg-slate-50/70 text-slate-500 font-semibold border-b border-slate-200">
                <tr>
                  <th className="px-5 py-3 min-w-[200px]">Employee</th>
                  <th className="px-3 py-3 w-44">First Name</th>
                  <th className="px-3 py-3 w-44">Last Name</th>
                  <th className="px-3 py-3 w-36">Employee No.</th>
                  <th className="px-3 py-3 w-32">Initials</th>
                  <th className="px-3 py-3 w-16 text-center">Work %</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {profiles.map(p => {
                  const curData = masterData[p.id] || {
                    employeeNumber: p.employeeNumber || '',
                    initials: p.initials || '',
                    firstName: p.firstName || '',
                    lastName: p.lastName || ''
                  };

                  return (
                    <tr key={p.id} className="hover:bg-slate-50/60 transition-colors">
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-3">
                          <UserAvatar
                            profile={p}
                            firstName={curData.firstName}
                            lastName={curData.lastName}
                            initials={curData.initials}
                            size="sm"
                          />
                          <div className="min-w-0">
                            <span className="font-semibold text-slate-800 block truncate max-w-[200px]" title={p.email}>
                              {p.email}
                            </span>
                            <span className="text-[10px] text-slate-400 capitalize">{p.role}</span>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <input
                          type="text"
                          aria-label={`First name for ${p.email}`}
                          value={curData.firstName}
                          onChange={e => {
                            const val = e.target.value;
                            setMasterData(prev => ({
                              ...prev,
                              [p.id]: {
                                ...(prev[p.id] || curData),
                                firstName: val
                              }
                            }));
                          }}
                          placeholder="First name"
                          disabled={isSavingMasterData}
                          className="w-full px-2.5 py-1.5 text-xs rounded-lg border border-slate-300 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none bg-white shadow-2xs disabled:opacity-60"
                        />
                      </td>
                      <td className="px-3 py-3">
                        <input
                          type="text"
                          aria-label={`Last name for ${p.email}`}
                          value={curData.lastName}
                          onChange={e => {
                            const val = e.target.value;
                            setMasterData(prev => ({
                              ...prev,
                              [p.id]: {
                                ...(prev[p.id] || curData),
                                lastName: val
                              }
                            }));
                          }}
                          placeholder="Last name"
                          disabled={isSavingMasterData}
                          className="w-full px-2.5 py-1.5 text-xs rounded-lg border border-slate-300 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none bg-white shadow-2xs disabled:opacity-60"
                        />
                      </td>
                      <td className="px-3 py-3">
                        <input
                          type="text"
                          aria-label={`Employee number for ${p.email}`}
                          value={curData.employeeNumber}
                          onChange={e => {
                            const val = e.target.value;
                            setMasterData(prev => ({
                              ...prev,
                              [p.id]: {
                                ...(prev[p.id] || curData),
                                employeeNumber: val
                              }
                            }));
                          }}
                          placeholder="1234567"
                          disabled={isSavingMasterData}
                          className="w-full px-2.5 py-1.5 text-xs font-mono rounded-lg border border-slate-300 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none bg-white shadow-2xs disabled:opacity-60"
                        />
                      </td>
                      <td className="px-3 py-3">
                        <input
                          type="text"
                          aria-label={`Initials for ${p.email}`}
                          value={curData.initials}
                          onChange={e => {
                            const val = e.target.value.toUpperCase();
                            setMasterData(prev => ({
                              ...prev,
                              [p.id]: {
                                ...(prev[p.id] || curData),
                                initials: val
                              }
                            }));
                          }}
                          placeholder="ABC"
                          maxLength={6}
                          disabled={isSavingMasterData}
                          className="w-full px-2.5 py-1.5 text-xs font-mono uppercase rounded-lg border border-slate-300 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none bg-white shadow-2xs disabled:opacity-60"
                        />
                      </td>
                      <td className="px-3 py-3 text-center">
                        <button
                          type="button"
                          onClick={() => setSelectedPercentageEmployee(p)}
                          className="inline-flex items-center justify-center p-1.5 rounded-lg border border-slate-200 bg-white hover:bg-indigo-50 hover:border-indigo-200 text-slate-600 hover:text-indigo-600 shadow-2xs transition cursor-pointer"
                          title={`Set monthly work percentages for ${curData.firstName || p.email}`}
                          aria-label={`Set work percentage for ${curData.firstName || p.email}`}
                        >
                          <Percent className="w-4 h-4 text-indigo-600" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {profiles.length === 0 && (
                  <tr>
                    <td colSpan={6} className="text-center py-6 text-slate-400 text-xs italic">
                      No employee profiles loaded.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="p-4 bg-slate-50 border-t border-slate-200 flex items-center justify-between">
            <div>
              {masterDataSaved && (
                <span className="text-xs text-emerald-600 font-semibold inline-flex items-center gap-1.5">
                  <Check className="w-4 h-4" /> All changes saved successfully
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={handleSaveMasterData}
              disabled={isSavingMasterData || profiles.length === 0}
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800 text-white font-semibold text-sm shadow-xs transition disabled:opacity-50 cursor-pointer"
            >
              {isSavingMasterData ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              <span>Save Master Data</span>
            </button>
          </div>
        </section>

        {selectedPercentageEmployee && (
          <WorkPercentageModal
            isOpen={Boolean(selectedPercentageEmployee)}
            onClose={() => setSelectedPercentageEmployee(null)}
            employee={selectedPercentageEmployee}
          />
        )}
      </div>
    );
  }

  // Regular User Settings View
  return (
    <div className="max-w-4xl mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div>
        <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Settings</h1>
        <p className="text-slate-500 mt-1">Manage your position visibility and standard description texts.</p>
      </div>

      {/* Positions Section */}
      <section className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="p-6 border-b border-slate-200 bg-slate-50 flex justify-between items-center">
          <h2 className="text-lg font-semibold text-slate-800">My Positions</h2>
          <span className="text-xs font-medium bg-slate-200 text-slate-600 px-2 py-1 rounded-full">{displayPositions.length}</span>
        </div>
        <div className="p-6 space-y-6">
          <ul className="space-y-2">
            {displayPositions.map(p => (
              <ProjectItem
                key={p.id}
                project={p}
                onUpdate={(name, text, visible) => updateProject(p.id, name, text, visible)}
              />
            ))}
            {displayPositions.length === 0 && (
              <div className="text-center py-6 text-slate-400 text-sm">No positions assigned yet.</div>
            )}
          </ul>
        </div>
      </section>

      <ImportExcel />
    </div>
  );
}
